import { NetworkError, request } from '../sync/api.js';
import { db } from '../sync/db.js';
import { enqueue } from '../sync/outbox.js';
import type { AttendanceAppointment, AttendanceList } from './types.js';

/**
 * Список записанных на день.
 *
 * При связи забирается с сервера и кладётся в локальную базу. Без связи
 * работает то, что успели забрать: экскурсия идёт по цеху, где сети нет,
 * а отметить явку надо в ту же минуту.
 */
export async function loadAttendanceList(date: string): Promise<AttendanceList | undefined> {
  try {
    const response = await request<{ date: string; appointments: AttendanceAppointment[] }>(
      `/api/hr/appointments?date=${date}`,
      { method: 'GET' },
      20_000,
    );
    const list: AttendanceList = {
      date: response.date,
      appointments: response.appointments,
      fetchedAt: new Date().toISOString(),
    };
    await db.attendanceLists.put(list);
    return list;
  } catch (error) {
    if (error instanceof NetworkError) {
      return db.attendanceLists.get(date);
    }
    throw error;
  }
}

export async function cachedAttendanceList(date: string): Promise<AttendanceList | undefined> {
  return db.attendanceLists.get(date);
}

/**
 * Отметка явки.
 *
 * Уходит событием в общую очередь, поэтому переживает обрыв связи и
 * перезагрузку телефона. Локальный список правится сразу — администратор
 * должен видеть результат нажатия, а не ждать сервер.
 */
export async function markAttendance(
  date: string,
  appointmentId: string,
  candidateId: string,
  showed: boolean,
  actorUserId: string,
): Promise<void> {
  await enqueue({
    type: 'CANDIDATE_ATTENDANCE_MARKED',
    aggregateType: 'CANDIDATE',
    aggregateId: candidateId,
    userId: actorUserId,
    payload: { appointmentId, candidateId, showed },
  });

  const list = await db.attendanceLists.get(date);
  if (!list) return;

  await db.attendanceLists.put({
    ...list,
    appointments: list.appointments.map((appointment) =>
      appointment.id !== appointmentId
        ? appointment
        : {
            ...appointment,
            slots: appointment.slots.map((slot) =>
              slot.candidateId === candidateId
                ? { ...slot, attendance: showed ? ('SHOWED' as const) : ('NO_SHOW' as const) }
                : slot,
            ),
          },
    ),
  });
}
